/* eslint-disable react/prop-types */

import React, { useState } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Popover,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import SoftAlert from "components/SoftAlert";

import useOvertimeStore from "store/useOvertimeStore";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export default function OvertimeDrawer({ open, onClose }) {
  const { addOvertimeRequest, actionLoading } = useOvertimeStore();

  const today = dayjs();
  const tomorrow = dayjs().add(1, "day");

  const [formData, setFormData] = useState({
    date: today,
    startTime: "",
    endTime: "",
    reason: "",
  });

  const [anchorCal, setAnchorCal] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");

  const [snack, setSnack] = useState({
    open: false,
    color: "success",
    message: "",
  });

  const validateForm = () => {
    let err = {};

    if (!formData.date)
      err.date = "Select a date";

    if (
      formData.date.isBefore(today, "day") ||
      formData.date.isAfter(tomorrow, "day")
    )
      err.date = "You can request only today or tomorrow.";

    if (!formData.startTime)
      err.startTime = "Start time required";

    if (!formData.endTime)
      err.endTime = "End time required";

    if (
      formData.startTime &&
      formData.endTime &&
      new Date(`2000-01-01T${formData.endTime}`) <=
      new Date(`2000-01-01T${formData.startTime}`)
    ) {
      err.endTime = "End time must be after start time";
    }

    if (!formData.reason.trim())
      err.reason = "Reason required";

    setErrors(err);

    return Object.keys(err).length === 0;
  };

  const calculateMinutes = () => {
    if (!formData.startTime || !formData.endTime) return 0;

    const start = new Date(`2000-01-01T${formData.startTime}`);
    const end = new Date(`2000-01-01T${formData.endTime}`);

    return Math.round((end - start) / 60000);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      attendance_date: formData.date.format("YYYY-MM-DD"),
      reason: formData.reason,
      expected_minutes: calculateMinutes(),
    };

    try {
      setApiError("");

      const result = await addOvertimeRequest(payload);

      if (!result.success) {
        setApiError(result.error || "Request failed");
        setSnack({
          open: true,
          color: "error",
          message: result.error || "Request failed",
        });
        return;
      }

      setSnack({
        open: true,
        color: "success",
        message: "Overtime request submitted successfully!",
      });

      setTimeout(() => {
        onClose();
        resetForm();
      }, 1200);

    } catch (err) {
      setApiError("Something went wrong");
      setSnack({
        open: true,
        color: "error",
        message: "Something went wrong. Please try again.",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      date: today,
      startTime: "",
      endTime: "",
      reason: "",
    });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 420,
          p: 3,
          borderRadius: "12px 0 0 12px",
          overflowY: "auto",
          zIndex: 99999,
        },
      }}
    >

      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" mb={3}>
        <SoftTypography variant="h4" fontWeight="medium">
          Overtime Request
        </SoftTypography>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <SoftSnackbar
        color={snack.color}
        icon="notifications"
        title="Overtime Request"
        content={snack.message}
        open={snack.open}
        close={() => setSnack({ ...snack, open: false })}
        time={3000}
      />

      {/* DATE FIELD */}
      <SoftTypography variant="h6">
        Select Date
      </SoftTypography>

      <SoftBox
        onClick={(e) => setAnchorCal(e.currentTarget)}
        sx={{
          border: 1,
          borderRadius: 2,
          borderColor: "gray",
          px: 2,
          py: 1,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mt: 1,
          "&:hover": { borderColor: "secondary" }
        }}
      >
        <CalendarTodayIcon fontSize="small" color="gray" />
        <SoftTypography variant="body2" fontWeight="medium">
          {formData.date.format("DD-MMM-YYYY")}
        </SoftTypography>
      </SoftBox>

      {errors.date && (
        <SoftTypography sx={{ color: "red", fontSize: 12, mt: 0.5 }}>
          {errors.date}
        </SoftTypography>
      )}

      <Popover
        open={Boolean(anchorCal)}
        anchorEl={anchorCal}
        onClose={() => setAnchorCal(null)}
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            borderRadius: 2,
            boxShadow: 3,
            p: 1,
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            value={formData.date}
            minDate={today}
            maxDate={tomorrow}
            onChange={(newDate) => {
              handleInputChange("date", newDate);
              setAnchorCal(null);
            }}
          />
        </LocalizationProvider>
      </Popover>

      {/* TIME FIELDS */}
      <SoftBox mt={3}>
        <SoftTypography variant="h6">Select Time</SoftTypography>

        <SoftBox display="flex" gap={2}>
          <SoftBox flex={1}>
            <SoftInput
              label="Start Time"
              type="time"
              value={formData.startTime}
              onChange={(e) => handleInputChange("startTime", e.target.value)}
            />
            {errors.startTime && (
              <SoftTypography sx={{ color: "red", fontSize: 12 }}>
                {errors.startTime}
              </SoftTypography>
            )}
          </SoftBox>

          <SoftBox flex={1}>
            <SoftInput
              label="End Time"
              type="time"
              value={formData.endTime}
              onChange={(e) => handleInputChange("endTime", e.target.value)}
            />
            {errors.endTime && (
              <SoftTypography sx={{ color: "red", fontSize: 12 }}>
                {errors.endTime}
              </SoftTypography>
            )}
          </SoftBox>
        </SoftBox>

        {formData.startTime && formData.endTime && (
          <SoftTypography mt={1}>
            Total: <b>{calculateMinutes()} minutes</b>
          </SoftTypography>
        )}
      </SoftBox>

      {/* REASON */}
      <SoftBox my={3}>
        <SoftInput
          label="Reason"
          multiline
          rows={3}
          value={formData.reason}
          onChange={(e) => handleInputChange("reason", e.target.value)}
        />

        {errors.reason && (
          <SoftTypography sx={{ color: "red", fontSize: 12 }}>
            {errors.reason}
          </SoftTypography>
        )}
      </SoftBox>

      <SoftButton
        fullWidth
        color="info"
        variant="gradient"
        onClick={handleSubmit}
        disabled={actionLoading}
        startIcon={!actionLoading && <AddIcon />}
      >
        {actionLoading ? "Submitting..." : "Submit Request"}
      </SoftButton>
    </Drawer>
  );
}
